import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react";
import { apiRequest } from "../lib/api";

interface AccessGuardResponse {
  showVpnWarning: boolean;
  countryCode: string | null;
  source: "header" | "lookup" | "unavailable";
}

interface AccessGuardContextValue {
  showVpnWarning: boolean;
  countryCode: string | null;
}

const ACCESS_GUARD_CACHE_KEY = "izumrud-access-guard";
const ACCESS_GUARD_CACHE_TTL_MS = 30 * 60 * 1000;
const defaultValue: AccessGuardContextValue = {
  showVpnWarning: false,
  countryCode: null
};
const AccessGuardContext = createContext<AccessGuardContextValue>(defaultValue);

function readCachedAccessGuard() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(ACCESS_GUARD_CACHE_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as {
      expiresAt?: number;
      showVpnWarning?: boolean;
      countryCode?: string | null;
    };

    if (!parsed.expiresAt || parsed.expiresAt < Date.now()) {
      window.sessionStorage.removeItem(ACCESS_GUARD_CACHE_KEY);
      return null;
    }

    return {
      showVpnWarning: Boolean(parsed.showVpnWarning),
      countryCode: typeof parsed.countryCode === "string" ? parsed.countryCode : null
    } satisfies AccessGuardContextValue;
  } catch {
    return null;
  }
}

function cacheAccessGuard(value: AccessGuardContextValue) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      ACCESS_GUARD_CACHE_KEY,
      JSON.stringify({
        ...value,
        expiresAt: Date.now() + ACCESS_GUARD_CACHE_TTL_MS
      })
    );
  } catch {
    // Ignore cache write failures.
  }
}

export function AccessGuardProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState<AccessGuardContextValue>(() => readCachedAccessGuard() ?? defaultValue);

  useEffect(() => {
    let isCancelled = false;

    const loadAccessGuard = async () => {
      try {
        const response = await apiRequest<AccessGuardResponse>("/api/runtime/access");

        if (isCancelled) {
          return;
        }

        const nextState = {
          showVpnWarning: Boolean(response.showVpnWarning),
          countryCode: response.countryCode
        } satisfies AccessGuardContextValue;

        setState(nextState);
        cacheAccessGuard(nextState);
      } catch (error) {
        if (!isCancelled) {
          console.error("Failed to resolve access guard state", error);
        }
      }
    };

    void loadAccessGuard();

    return () => {
      isCancelled = true;
    };
  }, []);

  const value = useMemo(() => state, [state]);
  return <AccessGuardContext.Provider value={value}>{children}</AccessGuardContext.Provider>;
}

export function useAccessGuard() {
  return useContext(AccessGuardContext);
}
